import { GITLAB_PIPELINES_PROXY_URL } from '../utils/constants/index.esm.js';

class Client {
  discoveryApi;
  proxyPath;
  fetchApi;
  gitlabAuthApi;
  constructor(opts) {
    this.discoveryApi = opts.discoveryApi;
    this.proxyPath = opts.proxyPath ?? GITLAB_PIPELINES_PROXY_URL;
    this.fetchApi = opts.fetchApi;
    this.gitlabAuthApi = opts.gitlabAuthApi;
  }
  async fetch(input, gitlabReposlug, init) {
    const token = await this.gitlabAuthApi.getAccessToken([
      "read_user",
      "api",
      "read_api",
      "read_repository"
    ]);
    const apiUrl = await this.apiUrl(gitlabReposlug);
    const resp = await this.fetchApi.fetch(`${apiUrl}${input}`, {
      ...init,
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    if (!resp.ok) {
      throw new Error(`Request failed - ${(await resp.json()).message}`);
    }
    return await resp.json();
  }
  async apiUrl(gitlabReposlug) {
    const baseUrl = await this.discoveryApi.getBaseUrl("proxy");
    return `${baseUrl}${this.proxyPath}/projects/${encodeURIComponent(gitlabReposlug)}`;
  }
  async listBranchesFromRepo(gitlabReposlug) {
    return await this.fetch(`/repository/branches`, gitlabReposlug);
  }
  async listProjectPipelines(gitlabReposlug, branch) {
    const response = await this.fetch(`/pipelines?ref=${branch}`, gitlabReposlug);
    return response;
  }
  async getSinglePipeline(gitlabReposlug, pipelineId, branch) {
    const response = await this.fetch(`/pipelines/${pipelineId}?ref=${branch}`, gitlabReposlug);
    return response;
  }
  async getLatestPipeline(gitlabReposlug, branch) {
    const response = await this.fetch(`/pipelines/latest?ref=${branch}`, gitlabReposlug);
    return response;
  }
  async runNewPipeline(gitlabReposlug, branch, variables) {
    const requestBody = {
      ref: branch,
      variables
    };
    const response = await this.fetch(
      `/pipeline?ref=${branch}`,
      gitlabReposlug,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(requestBody)
      }
    );
    return response;
  }
  async runNewPipelineWithTrigger(gitlabReposlug, triggerToken, branch) {
    const requestBody = {
      token: triggerToken,
      ref: branch
    };
    const response = await this.fetch(
      `/trigger/pipeline`,
      gitlabReposlug,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(requestBody)
      }
    );
    return response;
  }
  async retryPipelineJobs(gitlabReposlug, pipelineId) {
    const response = await this.fetch(
      `/pipelines/${pipelineId}/retry`,
      gitlabReposlug,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        }
      }
    );
    return response;
  }
  async cancelPipelineJobs(gitlabReposlug, pipelineId, branch) {
    const response = await this.fetch(
      `/pipelines/${pipelineId}/cancel?ref=${branch}`,
      gitlabReposlug,
      {
        method: "POST"
      }
    );
    return response;
  }
  async listPipelineJobs(gitlabReposlug, pipelineId, branch) {
    const response = await this.fetch(`/pipelines/${pipelineId}/jobs?ref=${branch}`, gitlabReposlug);
    return response;
  }
  async getSingleJob(gitlabReposlug, jobId, branch) {
    const response = await this.fetch(`/jobs/${jobId}?ref=${branch}`, gitlabReposlug);
    return response;
  }
  async runJob(gitlabReposlug, jobId, params, branch) {
    const requestBody = {
      job_variables_attributes: params
    };
    const response = await this.fetch(
      `/jobs/${jobId}/play?ref=${branch}`,
      gitlabReposlug,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(requestBody)
      }
    );
    return response;
  }
  async cancelJob(gitlabReposlug, jobId, branch) {
    const response = await this.fetch(
      `/jobs/${jobId}/cancel?ref=${branch}`,
      gitlabReposlug,
      {
        method: "POST"
      }
    );
    return response;
  }
  async retryJob(gitlabReposlug, jobId, branch) {
    const response = await this.fetch(
      `/jobs/${jobId}/retry?ref=${branch}`,
      gitlabReposlug,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        }
      }
    );
    return response;
  }
}
class GitlabPipelinesApiClient {
  client;
  constructor(opts) {
    this.client = new Client(opts);
  }
  async listBranchesFromRepo(gitlabReposlug) {
    return this.client.listBranchesFromRepo(gitlabReposlug);
  }
  async listProjectPipelines(gitlabReposlug, branch) {
    return this.client.listProjectPipelines(gitlabReposlug, branch);
  }
  async getSinglePipeline(gitlabReposlug, pipelineId, branch) {
    return this.client.getSinglePipeline(gitlabReposlug, pipelineId, branch);
  }
  async getLatestPipeline(gitlabReposlug, branch) {
    return this.client.getLatestPipeline(gitlabReposlug, branch);
  }
  async runNewPipeline(gitlabReposlug, branch, variables) {
    return this.client.runNewPipeline(gitlabReposlug, branch, variables);
  }
  async runNewPipelineWithTrigger(gitlabReposlug, triggerToken, branch) {
    return this.client.runNewPipelineWithTrigger(gitlabReposlug, triggerToken, branch);
  }
  async retryPipelineJobs(gitlabReposlug, pipelineId) {
    return this.client.retryPipelineJobs(gitlabReposlug, pipelineId);
  }
  async cancelPipelineJobs(gitlabReposlug, pipelineId, branch) {
    return this.client.cancelPipelineJobs(gitlabReposlug, pipelineId, branch);
  }
  async listPipelineJobs(gitlabReposlug, pipelineId, branch) {
    return this.client.listPipelineJobs(gitlabReposlug, pipelineId, branch);
  }
  async getSingleJob(gitlabReposlug, jobId, branch) {
    return this.client.getSingleJob(gitlabReposlug, jobId, branch);
  }
  async runJob(gitlabReposlug, jobId, params, branch) {
    return this.client.runJob(gitlabReposlug, jobId, params, branch);
  }
  async cancelJob(gitlabReposlug, jobId, branch) {
    return this.client.cancelJob(gitlabReposlug, jobId, branch);
  }
  async retryJob(gitlabReposlug, jobId, branch) {
    return this.client.retryJob(gitlabReposlug, jobId, branch);
  }
}

export { GitlabPipelinesApiClient };
//# sourceMappingURL=GitlabPipelinesClient.esm.js.map
